import React from 'react';
import classnames from 'classnames';
import { SelectroController, TemplateResolution } from '@aha-app/selectro-v2';
import renderIf from 'render-if';
import { useController, ApplicationView } from '@aha-app/mvc';
import type { Item } from '@aha-app/selectro-v2';
import { resolveAttribute } from '../helpers';

interface SelectroV2TagsTriggerTemplateProps {
  noneSelected?: string;
  items: Item[];
  className?: string;
  editable?: boolean;
  callSelectCallbackOnRemove?: boolean;
}

// Renders an icon if it exists
const ItemsIcon = ({ icon }) => {
  if (!icon) return null;
  return (
    <i
      className={`fa-solid fa-${icon.type}`}
      style={{ color: icon.style?.color }}
      role='img'
    />
  );
};

// Renders a reference number if it exists
const ItemsRef = ({ refNum }) => {
  if (!refNum) return null;
  return (
    <>
      <span className='refnum'>{refNum}</span>{' '}
    </>
  );
};

// Renders the shipped icon if shipped exists
const ItemsShipped = ({ shipped }) => {
  if (!shipped) return null;
  return <i className='fa-regular fa-circle-check shipped' />;
};

// Renders an aha-avatar if avatar and initials exists
const ItemsAvatar = ({ avatar, initials }) => {
  if (typeof avatar && !initials) return null;
  return <aha-avatar size={16} src={avatar || null} label={initials} />;
};

// Renders the item text with a link or tooltip if they exist
const Text = ({ item }) => {
  const link = resolveAttribute(item, 'link');
  const text = resolveAttribute(item, 'text');
  const tooltip = resolveAttribute(item, 'tooltip');

  const linkProps = link && {
    ...(link.drawer && { 'data-drawer-url': link.url }),
    ...(link.new_tab && { target: '_blank', rel: 'noopener noreferrer' }),
  };

  return (
    <>
      {link ? (
        <a href={link.url} {...linkProps}>
          {text}
        </a>
      ) : (
        <span>{text}</span>
      )}
      {renderIf(tooltip)(
        <aha-tooltip style={{ '--Tooltip__tooltip--max-width': '200px' }}>
          {tooltip}
        </aha-tooltip>
      )}
    </>
  );
};

const SelectroV2TagsTriggerTemplate: React.FC<
  SelectroV2TagsTriggerTemplateProps
> = ({
  items,
  className = 'selectro-trigger',
  editable = true,
  noneSelected,
  callSelectCallbackOnRemove,
}) => {
  const controller = useController(SelectroController);
  return (
    <button
      type='button'
      className={`selectro-v2-template--tags-trigger ${className}`}
    >
      {items.length ? (
        <TemplateResolution
          template={controller.state.templates.triggerItems}
          defaultTemplate={() => (
            <div className='selectro-v2-template__tags__list'>
              {items.map(item => {
                const color = resolveAttribute(item, 'color')
                  ? resolveAttribute(item, 'color')
                  : controller?.state.defaultTagsColor || null;
                return (
                  <div
                    key={item.value}
                    className={classnames('selectro-v2-template__tags__item', {
                      'selectro-v2-template__tags__item--with-background':
                        color,
                    })}
                    {...(color && {
                      style: {
                        backgroundColor: color,
                        color: 'var(--theme-inverted-primary-text)',
                      },
                    })}
                  >
                    <div className='selectro-v2-template__tags__item-label'>
                      <ItemsIcon icon={resolveAttribute(item, 'icon')} />
                      <ItemsRef refNum={resolveAttribute(item, 'ref')} />
                      <span
                        className={classnames({
                          'selectro-v2-template__tags__item-label--text': true,
                          strikethrough: resolveAttribute(item, 'wont_do'),
                        })}
                      >
                        <ItemsShipped
                          shipped={resolveAttribute(item, 'shipped')}
                        />
                        <ItemsAvatar
                          avatar={resolveAttribute(item, 'avatar')}
                          initials={resolveAttribute(item, 'initials')}
                        />
                        <Text item={item} />
                      </span>
                    </div>
                    {editable && controller && (
                      <a
                        className='selectro-v2-template__tags__item-remove'
                        data-testid={`selectro-v2-tag-remove__${item.value}`}
                        onClick={e => {
                          e.preventDefault();
                          const newSelection = controller.selected.filter(
                            selectedItem => selectedItem.value !== item.value
                          );
                          controller.actionSetSelected(newSelection);
                          // When removing an item, we need to ensure the selected callback is called
                          if (callSelectCallbackOnRemove) {
                            controller.callSelectCallback({
                              item,
                              persistChange: true, // This tells react-in-place to persist the change immediately.
                            });
                          }
                        }}
                      >
                        <svg
                          height='14'
                          width='14'
                          viewBox='0 0 20 20'
                          focusable='false'
                        >
                          <path d='M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z' />
                        </svg>
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        />
      ) : (
        <span style={{ color: 'var(--theme-tertiary-text)' }}>
          {typeof noneSelected !== 'undefined'
            ? noneSelected
            : controller?.state.text.noneSelected}
        </span>
      )}
    </button>
  );
};
export default ApplicationView(SelectroV2TagsTriggerTemplate);
